// Slice 23+ — CGC member admin roster (DESIGN §6.6, §3.3.4). Slice 24 added
// admin-driven invite / edit / resend / revoke so onboarding no longer
// requires `wrangler d1 execute`. Suspending a member blocks sign-in and
// excludes them from incident fan-out; reactivating restores both.
//
// Slice 35 — `Placeholder` lifecycle (PROJECT §5.4). Placeholders are stub
// identities that engineering uses pre-paperwork; they behave like Active
// for auth + workflow purposes but the `SystemPromptLoader` env-gate refuses
// their approvals in production. UI affordances added in this slice:
//   • amber `Placeholder` pill with a warning icon;
//   • Placeholder option in the status filter chips + invite form;
//   • name / email / reason fields on the Edit panel (legal-paperwork rename
//     writes through the new `cgc_member_identity_changes` audit table);
//   • Promote button visible only on Placeholder rows;
//   • identity-history drawer per member.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import type { AdminCgcMemberSummary, CgcMemberIdentityChangeEntry } from '@besideu/shared/admin';
import {
  InviteCgcMemberSchema,
  UpdateCgcMemberIdentitySchema,
  UpdateCgcMemberSchema,
  type InviteCgcMemberRequest,
  type PromoteCgcMemberRequest,
  type UpdateCgcMemberIdentityRequest,
  type UpdateCgcMemberRequest,
} from '@besideu/shared/admin';
import {
  CGC_MEMBER_KINDS,
  PROMPT_MODE_APPROVAL_REQUIREMENTS,
  type CgcMemberKind,
  type PromptApprovalMode,
} from '@besideu/shared/maeve';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, Select, TextInput } from '@/components/ui/Field.tsx';

type StatusFilter = 'all' | 'Active' | 'Suspended' | 'Placeholder';

// Pre-compute the lookup so we don't iterate the requirements object on every
// kind change in the invite/edit dialogs.
const MODES_FOR_KIND: Record<CgcMemberKind, readonly string[]> = (() => {
  const result = {
    grief_counsellor: [],
    mhp: [],
    gp_psychiatrist: [],
    legal_counsel: [],
    product: [],
  } as Record<CgcMemberKind, string[]>;
  for (const [mode, req] of Object.entries(PROMPT_MODE_APPROVAL_REQUIREMENTS)) {
    for (const kind of req.requiredKinds) result[kind].push(mode);
  }
  return result;
})();

// Plain-English label + description per CGC member kind. Keys are the
// wire-format identifiers stored in `cgc_members.kind`; the values are
// admin-facing copy used by the dropdown (label) and the contextual hint
// (description). The default `replaceAll('_', ' ')` we used before was
// inconsistent — `mhp` and `gp_psychiatrist` aren't readable without
// expansion, and admins shouldn't have to guess which adviser type each
// represents.
const KIND_INFO: Record<CgcMemberKind, { label: string; description: string }> = {
  grief_counsellor: {
    label: 'Grief counsellor',
    description:
      'Bereavement support specialist. Approves Manager_Guidance prompts (single sign-off).',
  },
  mhp: {
    label: 'Mental health professional',
    description:
      'Clinician (psychologist, mental health nurse, etc.). Joint sign-off with GP/psychiatrist for Companion + Trajectory prompts.',
  },
  gp_psychiatrist: {
    label: 'GP / Psychiatrist',
    description:
      'Medical doctor (GP or psychiatrist). Joint sign-off with mental health professional for Companion + Trajectory prompts.',
  },
  legal_counsel: {
    label: 'Legal counsel',
    description:
      'Solicitor or legal adviser. Approves HR_Maeve, Policy_Audit (and sub-agents), and AI_Disclosure prompts.',
  },
  product: {
    label: 'Product',
    description: 'Internal product team. Does not currently approve any system-prompt modes.',
  },
};

// Safe lookup for the table cell — `member.kind` comes off the wire as
// `string`, so an unknown kind (deployment skew between API and SPA)
// renders the raw value as a graceful fallback rather than displaying
// `undefined`.
function kindLabel(kind: string): string {
  return KIND_INFO[kind as CgcMemberKind]?.label ?? kind;
}

// Plain-English descriptions surfaced next to each mode checkbox. The keys
// here are the wire-format mode identifiers (with underscores, matching
// `PROMPT_MODE_APPROVAL_REQUIREMENTS` / `system_prompts.mode`); the values
// are admin-facing copy. BesideU Admins are a small ops team — they shouldn't
// have to memorise the DESIGN-doc taxonomy to invite a CGC member.
// Typed as a full `Record<PromptApprovalMode, string>` (not `Partial`)
// so a new prompt mode added to `PROMPT_MODE_APPROVAL_REQUIREMENTS` here
// triggers a TS error: "Property 'New_Mode' is missing in type". This
// closes the bug the QA judge slice's code-review pass surfaced — modes
// were silently appearing as bare identifiers in the CGC invite UI
// without copy. Update this map whenever a new mode is added.
const MODE_DESCRIPTIONS: Record<PromptApprovalMode, string> = {
  HR_Maeve: 'AI assistant for HR Admins — policy questions and case context.',
  Manager_Guidance:
    'Guidance for Managers — first-response drafts and ongoing chat about a bereaved employee.',
  Employee_Companion:
    'Async companion chat for bereaved Employees. Most sensitive surface (Article 9 data).',
  Policy_Audit:
    'Legacy single-agent policy audit. Kept for back-compat; new audits use the three sub-agents below.',
  Policy_Audit_Compliance:
    'Multi-agent policy audit — compliance reviewer (flags policy gaps and regulatory issues).',
  Policy_Audit_Empathy:
    'Multi-agent policy audit — empathy reviewer (flags tone and human-impact issues).',
  Policy_Audit_Supervisor:
    'Multi-agent policy audit — supervisor that synthesises the compliance + empathy findings.',
  Trajectory_Classifier:
    'Background safety classifier monitoring Companion session trajectory for red flags.',
  AI_Disclosure: 'User-facing AI disclosure text shown to Employees (Article 22 / DESIGN §4.7).',
  Companion_Wrap_Nudge:
    'Wrap-up nudge prepended after 15 minutes of Companion chat (DESIGN §5.6.3 #11).',
  Companion_Empathetic_Close:
    'Empathetic closing message when a Companion session ends (DESIGN §5.6.3 #12).',
  Boilerplate_Intended_Use:
    'Reusable Intended Use Statement block referenced by every Maeve user-mode prompt (§4.7.1).',
  Boilerplate_Human_Oversight:
    'Reusable Human Oversight Clause referenced by every Maeve user-mode prompt (§4.7.3).',
  Boilerplate_Prohibited_Use:
    'Reusable Prohibited Use Cases anchor referenced by every Maeve user-mode prompt (§4.7.2).',
  DPA: 'Data Processing Agreement click-through copy shown at HR self-serve signup (Slice 31).',
  DPIA: 'Data Protection Impact Assessment — legal-counsel sign-off, a PROJECT §5 hard gate.',
  QA_Judge:
    'LLM-as-judge prompt that scores Companion sessions inline at session end against the 10-dimension rubric (QA sampler foundation slice).',
  QA_Redactor:
    'LLM-as-redactor prompt that strips identifiers from opt-in Companion session snapshots before they reach the CGC scoring pool (QA sampler redactor slice).',
  // PROJECT §4 Step 2 — key-rule extraction prompt. Single legal_counsel
  // sign-off. On policy upload, surfaces the discrete rules HR needs to
  // review and approve before the policy can be `Approved` (and Maeve
  // can cite it for entitlements).
  Policy_KeyRule_Extraction:
    'LLM extraction prompt that surfaces the discrete rules (e.g. "5 days paid leave for a spouse/parent bereavement") HR must review and approve before the policy can be Approved.',
};

export function AdminCgcMembers() {
  const queryClient = useQueryClient();
  const cgcMembers = useQuery({
    queryKey: ['admin', 'cgc-members'],
    queryFn: () => api.get<{ members: AdminCgcMemberSummary[] }>('/api/admin/cgc-members'),
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = (searchParams.get('status') ?? 'all') as StatusFilter;
  const queryText = searchParams.get('q') ?? '';
  const [inviting, setInviting] = useState(false);

  function setStatus(next: StatusFilter) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'all') sp.delete('status');
    else sp.set('status', next);
    setSearchParams(sp, { replace: true });
  }
  function setQuery(next: string) {
    const sp = new URLSearchParams(searchParams);
    if (!next) sp.delete('q');
    else sp.set('q', next);
    setSearchParams(sp, { replace: true });
  }

  const filtered = useMemo(() => {
    const all = cgcMembers.data?.members ?? [];
    const q = queryText.trim().toLowerCase();
    return all.filter((m) => {
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (!q) return true;
      return m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
    });
  }, [cgcMembers.data, statusFilter, queryText]);

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">CGC Members</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            External clinical / legal advisers who approve system prompts and review red-flag
            incidents. Inviting a member dispatches a magic link; they complete TOTP enrolment on
            first sign-in. Suspending preserves the row for legal-defensibility audit history.
          </p>
        </div>
        <Button
          variant="primary"
          className="shrink-0"
          onClick={() => setInviting((v) => !v)}
          aria-expanded={inviting}
        >
          {inviting ? 'Cancel invite' : 'Invite member'}
        </Button>
      </header>

      {inviting ? (
        <InviteCgcMemberPanel
          onDone={async () => {
            setInviting(false);
            await queryClient.invalidateQueries({ queryKey: ['admin', 'cgc-members'] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
          }}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1 rounded-md border border-border bg-background p-1">
          {(['all', 'Active', 'Placeholder', 'Suspended'] as const).map((s) => (
            <FilterChip
              key={s}
              active={statusFilter === s}
              onClick={() => setStatus(s)}
              label={s === 'all' ? 'All' : s}
              count={
                s === 'all'
                  ? cgcMembers.data?.members.length
                  : cgcMembers.data?.members.filter((m) => m.status === s).length
              }
            />
          ))}
        </div>
        <input
          type="search"
          value={queryText}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or email"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none sm:max-w-xs"
        />
      </div>

      {cgcMembers.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : cgcMembers.error ? (
        <p className="text-sm text-traffic-red">Could not load CGC members.</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          {cgcMembers.data.members.length === 0
            ? 'No CGC members enrolled yet.'
            : 'No members match the current filters.'}
        </div>
      ) : (
        <CgcMembersTable members={filtered} />
      )}
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded px-3 py-1.5 text-xs font-medium transition',
        active
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      ].join(' ')}
    >
      {label}
      {typeof count === 'number' ? (
        <span className={`ml-1.5 ${active ? 'opacity-70' : 'opacity-60'}`}>{count}</span>
      ) : null}
    </button>
  );
}

function CgcMembersTable({ members }: { members: AdminCgcMemberSummary[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium">Email</th>
            <th className="px-4 py-2 font-medium">Kind</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th
              className="px-4 py-2 font-medium"
              title="System-prompt approvals and rejections filed by this member."
            >
              Decisions
            </th>
            <th className="px-4 py-2 font-medium">Last sign-in</th>
            <th className="px-4 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <CgcMemberRow key={m.id} member={m} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CgcMemberRow({ member }: { member: AdminCgcMemberSummary }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  // Slice 35 — identity-history drawer. Lazy-loaded on toggle so the listing
  // round-trip is only paid when an admin actually wants to inspect rename
  // history (most rows have zero changes; pre-fetching every row's history
  // would be a wasted N+1 across a roster).
  const [showingHistory, setShowingHistory] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sent'>('idle');

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'cgc-members'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
  }

  const suspend = useMutation({
    mutationFn: () => api.post(`/api/admin/cgc-members/${member.id}/suspend`),
    onSuccess: () => {
      setError(null);
      invalidateAll();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : 'Suspend failed.');
    },
  });

  const reactivate = useMutation({
    mutationFn: () => api.post(`/api/admin/cgc-members/${member.id}/reactivate`),
    onSuccess: () => {
      setError(null);
      invalidateAll();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : 'Reactivate failed.');
    },
  });

  const resend = useMutation({
    mutationFn: () => api.post(`/api/admin/cgc-members/${member.id}/resend-invite`),
    onSuccess: () => {
      setError(null);
      setResendStatus('sent');
      window.setTimeout(() => setResendStatus('idle'), 3000);
    },
    onError: (e) => {
      setError(e instanceof ApiError ? translateResendError(e) : 'Resend failed.');
    },
  });

  const revoke = useMutation({
    mutationFn: () => api.del(`/api/admin/cgc-members/${member.id}`),
    onSuccess: () => {
      setError(null);
      invalidateAll();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? translateRevokeError(e) : 'Revoke failed.');
    },
  });

  // Slice 35 — one-way Placeholder → Active promotion. Optional reason field
  // captured in a prompt() to keep the row UI compact; the audit row carries
  // it through. Refused server-side if the member is no longer Placeholder
  // at the moment of the request (a concurrent promote landing between the
  // page load and the click); translated to a clear "no longer Placeholder"
  // copy via `translatePromoteError`.
  const promote = useMutation({
    mutationFn: (input: PromoteCgcMemberRequest) =>
      api.post<{ member: AdminCgcMemberSummary }>(
        `/api/admin/cgc-members/${member.id}/promote`,
        input,
      ),
    onSuccess: () => {
      setError(null);
      invalidateAll();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? translatePromoteError(e) : 'Promote failed.');
    },
  });

  // Pre-enrolment members can have their invite re-sent or revoked. Post-
  // enrolment OR any decisions → Suspend is the only offboarding path; the
  // revoke button stays visible-but-disabled (with title text) so the admin
  // understands the affordance + why it's unavailable for this row.
  // Suspended members can't have their invite re-sent: the auth flow filters on
  // `status='Active'`, so a resent link would be dead-on-arrival (the server
  // refuses with `suspended` too). Reactivate first, then resend.
  const canResend = !member.totpEnrolled && member.status !== 'Suspended';
  const canRevoke = !member.totpEnrolled && member.decisionCount === 0;
  const revokeDisabledReason = member.totpEnrolled
    ? 'Cannot revoke — member has signed in. Use Suspend instead.'
    : member.decisionCount > 0
      ? 'Cannot revoke — member has filed decisions. Use Suspend instead.'
      : undefined;

  return (
    <>
      <tr className="border-t border-border align-top">
        <td className="px-4 py-2 font-medium">{member.name}</td>
        <td className="px-4 py-2 text-muted-foreground">{member.email}</td>
        <td className="px-4 py-2 text-muted-foreground">{kindLabel(member.kind)}</td>
        <td className="px-4 py-2">
          <CgcMemberStatusPill member={member} />
        </td>
        <td className="px-4 py-2 text-muted-foreground">{member.decisionCount}</td>
        <td className="px-4 py-2 text-muted-foreground">
          {member.lastSignInAt ? new Date(member.lastSignInAt).toLocaleString() : '—'}
        </td>
        <td className="px-4 py-2">
          <div className="flex flex-wrap items-center justify-end gap-1">
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs"
              onClick={() => setEditing((v) => !v)}
              aria-expanded={editing}
              aria-label={`Edit CGC member ${member.name}`}
            >
              {editing ? 'Cancel' : 'Edit'}
            </Button>
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs"
              onClick={() => setShowingHistory((v) => !v)}
              aria-expanded={showingHistory}
              aria-label={`Show identity history for ${member.name}`}
            >
              {showingHistory ? 'Hide history' : 'History'}
            </Button>
            {member.status === 'Placeholder' ? (
              <Button
                variant="ghost"
                className="px-2 py-1 text-xs text-traffic-green-text"
                onClick={() => {
                  // Promotion is one-way and immediately makes every prior
                  // approval by this member valid in production (the loader
                  // re-reads `cgc_members.status` on every gate / activate
                  // call). Confirm before firing.
                  const reason = window.prompt(
                    `Promote ${member.name} to Active?\n\nThis closes the production env-gate on every prompt this member has approved. Identity should already match real-adviser paperwork (use Edit first if not).\n\nOptional reason (audit log):`,
                    '',
                  );
                  if (reason === null) return; // cancel
                  promote.mutate({ reason: reason.trim() || undefined });
                }}
                loading={promote.isPending}
                aria-label={`Promote CGC member ${member.name} to Active`}
              >
                Promote
              </Button>
            ) : null}
            {canResend ? (
              resendStatus === 'sent' ? (
                <span className="px-2 py-1 text-xs text-traffic-green-text" role="status">
                  Sent
                </span>
              ) : (
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  loading={resend.isPending}
                  onClick={() => resend.mutate()}
                  aria-label={`Resend invite to ${member.name}`}
                >
                  Resend invite
                </Button>
              )
            ) : null}
            {member.status === 'Active' ? (
              <Button
                variant="ghost"
                className="px-2 py-1 text-xs text-traffic-red"
                onClick={() => {
                  if (
                    window.confirm(
                      `Suspend ${member.name}? They will be unable to sign in or receive incident-review emails until reactivated. Their prior decisions remain in the audit log.`,
                    )
                  ) {
                    suspend.mutate();
                  }
                }}
                loading={suspend.isPending}
                aria-label={`Suspend CGC member ${member.name}`}
              >
                Suspend
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="px-2 py-1 text-xs"
                onClick={() => {
                  // Reactivating returns the member to the live red-flag
                  // fan-out (DESIGN §3.3.4 Article 9 data). A mis-click on
                  // the wrong row would re-expose a departed adviser to
                  // live clinical incidents — destructive in the inverse
                  // direction, so confirm() mirrors Suspend.
                  if (
                    window.confirm(
                      `Reactivate ${member.name}? They will resume receiving incident-review emails and be re-eligible for sign-in.`,
                    )
                  ) {
                    reactivate.mutate();
                  }
                }}
                loading={reactivate.isPending}
                aria-label={`Reactivate CGC member ${member.name}`}
              >
                Reactivate
              </Button>
            )}
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs text-traffic-red disabled:text-muted-foreground"
              disabled={!canRevoke}
              {...(revokeDisabledReason ? { title: revokeDisabledReason } : {})}
              onClick={() => {
                if (
                  window.confirm(
                    `Permanently delete the invite for ${member.name}? This cannot be undone. If they have signed in or filed decisions, use Suspend instead.`,
                  )
                ) {
                  revoke.mutate();
                }
              }}
              loading={revoke.isPending}
              aria-label={`Revoke invite for ${member.name}`}
            >
              Revoke
            </Button>
          </div>
        </td>
      </tr>
      {editing ? (
        <tr className="border-t border-dashed border-border bg-muted/20">
          <td colSpan={7} className="px-4 py-3">
            <EditCgcMemberRow
              member={member}
              onDone={() => {
                setEditing(false);
                invalidateAll();
              }}
            />
          </td>
        </tr>
      ) : null}
      {showingHistory ? (
        <tr className="border-t border-dashed border-border bg-muted/10">
          <td colSpan={7} className="px-4 py-3">
            <IdentityHistoryDrawer cgcMemberId={member.id} />
          </td>
        </tr>
      ) : null}
      {error && (
        <tr>
          <td
            colSpan={7}
            className="border-t border-border bg-traffic-red/5 px-4 py-2 text-xs text-traffic-red"
          >
            {error}
          </td>
        </tr>
      )}
      {member.status === 'Suspended' && member.suspendedAt && (
        <tr>
          <td
            colSpan={7}
            className="border-t border-border bg-muted/20 px-4 py-2 text-xs text-muted-foreground"
          >
            Suspended {new Date(member.suspendedAt).toLocaleString()}
            {member.suspendedByAdminName ? ` by ${member.suspendedByAdminName}` : ''}.
          </td>
        </tr>
      )}
      {member.status === 'Active' && member.promotedToActiveAt && (
        <tr>
          <td
            colSpan={7}
            className="border-t border-border bg-muted/20 px-4 py-2 text-xs text-muted-foreground"
          >
            Promoted from Placeholder {new Date(member.promotedToActiveAt).toLocaleString()}
            {member.promotedByAdminName ? ` by ${member.promotedByAdminName}` : ''}.
          </td>
        </tr>
      )}
    </>
  );
}

// Slice 35 — identity-history drawer for a single CGC member. Lazy-loaded
// (only fires the listing query when expanded). Read-only — the rename
// action lives in the Edit panel, this is the audit-trail view.
function IdentityHistoryDrawer({ cgcMemberId }: { cgcMemberId: string }) {
  const query = useQuery({
    queryKey: ['admin', 'cgc-members', cgcMemberId, 'identity-changes'],
    queryFn: () =>
      api.get<{ changes: CgcMemberIdentityChangeEntry[] }>(
        `/api/admin/cgc-members/${cgcMemberId}/identity-changes`,
      ),
  });

  if (query.isPending) {
    return <p className="text-xs text-muted-foreground">Loading identity history…</p>;
  }
  if (query.error) {
    return <p className="text-xs text-traffic-red">Could not load identity history.</p>;
  }
  const changes = query.data?.changes ?? [];
  if (changes.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No identity changes on file. Renames or email updates land here once they happen.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        Identity history (most recent first)
      </p>
      <ol className="space-y-2">
        {changes.map((c) => (
          <li key={c.id} className="rounded border border-border bg-background px-3 py-2 text-xs">
            <div className="text-muted-foreground">
              {new Date(c.createdAt).toLocaleString()}
              {c.actorAdminName
                ? ` · by ${c.actorAdminName}`
                : c.actorAdminId
                  ? ' · by admin (deleted)'
                  : ''}
            </div>
            {c.oldName !== c.newName && (
              <div>
                Name: <span className="text-muted-foreground line-through">{c.oldName}</span> →{' '}
                <span className="font-medium">{c.newName}</span>
              </div>
            )}
            {c.oldEmail !== c.newEmail && (
              <div>
                Email: <span className="text-muted-foreground line-through">{c.oldEmail}</span> →{' '}
                <span className="font-medium">{c.newEmail}</span>
              </div>
            )}
            {c.reason && <div className="mt-1 text-muted-foreground italic">"{c.reason}"</div>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function CgcMemberStatusPill({ member }: { member: AdminCgcMemberSummary }) {
  // Pre-enrolment Active members render as amber "Awaiting first sign-in" so
  // the admin can scan the roster and immediately spot who hasn't onboarded
  // yet. A green "Active" pill is reserved for members who have completed
  // TOTP — i.e. who are actually able to act on prompts / incidents.
  //
  // Slice 35 — `Placeholder` overrides the pre-enrolment / enrolment logic:
  // a Placeholder might be TOTP-enrolled (engineering signed in to test) but
  // the staging-only posture is the load-bearing fact, so the amber
  // `Placeholder` pill takes precedence over the green Active pill.
  const { label, className, title } =
    member.status === 'Suspended'
      ? {
          label: 'Suspended',
          className: 'bg-traffic-red/10 text-traffic-red',
          title: undefined as string | undefined,
        }
      : member.status === 'Placeholder'
        ? {
            label: '⚠ Placeholder',
            className: 'bg-traffic-amber/20 text-traffic-amber-text',
            title:
              'Stub identity — staging engineering only. Approvals are refused at activation / serving in production. Promote to Active once legal paperwork closes.',
          }
        : member.totpEnrolled
          ? {
              label: 'Active',
              className: 'bg-traffic-green/10 text-traffic-green-text',
              title: undefined,
            }
          : {
              label: 'Awaiting first sign-in',
              className: 'bg-traffic-amber/20 text-traffic-amber-text',
              title: undefined,
            };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
      {...(title ? { title } : {})}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Invite panel — shown at the top of the page when "Invite member" is open.
// ---------------------------------------------------------------------------

function InviteCgcMemberPanel({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<CgcMemberKind>('grief_counsellor');
  const [modesAllowed, setModesAllowed] = useState<string[]>([]);
  // Slice 35 — invite-as-Placeholder. Default Active preserves the original
  // sign-up-real-adviser flow; Placeholder is for pre-paperwork engineering
  // when the operator wants to drive end-to-end approval flows in staging
  // before the named adviser-of-record is appointed.
  const [status, setStatus] = useState<'Active' | 'Placeholder'>('Active');
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: (input: InviteCgcMemberRequest) =>
      api.post<{ member: AdminCgcMemberSummary }>('/api/admin/cgc-members', input),
    onSuccess: () => {
      onDone();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? translateInviteError(e) : 'Invite failed.');
    },
  });

  function submit() {
    setError(null);
    const parsed = InviteCgcMemberSchema.safeParse({ name, email, kind, modesAllowed, status });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError(issue ? humaniseInviteIssue(issue) : 'Invalid input.');
      return;
    }
    invite.mutate(parsed.data);
  }

  function toggleMode(mode: string) {
    setModesAllowed((prev) =>
      prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode],
    );
  }

  // When kind changes, drop any selected modes that are no longer valid for
  // the new kind. Without this the form would submit a stale allow-list and
  // hit the server-side 422 instead of self-correcting.
  function changeKind(next: CgcMemberKind) {
    setKind(next);
    const validForNext = new Set(MODES_FOR_KIND[next]);
    setModesAllowed((prev) => prev.filter((m) => validForNext.has(m)));
  }

  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4">
      <h2 className="text-base font-semibold">Invite CGC member</h2>
      <p className="mt-1 mb-4 text-xs text-muted-foreground">
        A magic-link email is sent on submit. The adviser completes TOTP enrolment on first sign-in.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name">
          {(id, desc) => (
            <TextInput
              id={id}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              {...desc}
            />
          )}
        </Field>
        <Field label="Email">
          {(id, desc) => (
            <TextInput
              id={id}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              {...desc}
            />
          )}
        </Field>
        <Field label="Kind" hint={KIND_INFO[kind].description}>
          {(id, desc) => (
            <Select
              id={id}
              value={kind}
              onChange={(e) => changeKind(e.target.value as CgcMemberKind)}
              {...desc}
            >
              {CGC_MEMBER_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_INFO[k].label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Restrict to specific modes (optional)"
          hint="Leave empty to allow every mode this kind can approve. Tick to narrow this member to a subset."
        >
          {(id, desc) => (
            <fieldset id={id} className="grid grid-cols-1 gap-2" {...desc}>
              {MODES_FOR_KIND[kind].length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  {KIND_INFO[kind].label} members do not currently approve any system-prompt modes.
                </p>
              ) : (
                MODES_FOR_KIND[kind].map((mode) => (
                  <label key={mode} className="flex items-start gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={modesAllowed.includes(mode)}
                      onChange={() => toggleMode(mode)}
                    />
                    <span>
                      <span className="font-medium">{mode}</span>
                      {MODE_DESCRIPTIONS[mode as PromptApprovalMode] ? (
                        <span className="block text-xs text-muted-foreground">
                          {MODE_DESCRIPTIONS[mode as PromptApprovalMode]}
                        </span>
                      ) : null}
                    </span>
                  </label>
                ))
              )}
            </fieldset>
          )}
        </Field>
        <Field
          label="Identity"
          hint="Placeholder = staging-only stub. Approvals are refused at activation / serving in production until you promote to Active. Use Placeholder only while real-adviser paperwork is in flight."
        >
          {(id, desc) => (
            <fieldset id={id} className="space-y-1.5" {...desc}>
              <label className="flex items-start gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="invite-status"
                  className="mt-0.5"
                  value="Active"
                  checked={status === 'Active'}
                  onChange={() => setStatus('Active')}
                />
                <span>
                  <span className="font-medium">Active</span>
                  <span className="block text-xs text-muted-foreground">
                    Standard invite. Real adviser of record.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="invite-status"
                  className="mt-0.5"
                  value="Placeholder"
                  checked={status === 'Placeholder'}
                  onChange={() => setStatus('Placeholder')}
                />
                <span>
                  <span className="font-medium">Placeholder</span>
                  <span className="block text-xs text-muted-foreground">
                    Pre-paperwork stub. Behaves like Active for sign-in and approvals, but the
                    production loader refuses to activate / serve any prompt approved by a
                    Placeholder.
                  </span>
                </span>
              </label>
            </fieldset>
          )}
        </Field>
      </div>
      {error ? (
        <p className="mt-3 text-sm text-traffic-red" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} loading={invite.isPending}>
          Send invite
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit sub-row — inline expansion under the row being edited.
// ---------------------------------------------------------------------------

function EditCgcMemberRow({
  member,
  onDone,
}: {
  member: AdminCgcMemberSummary;
  onDone: () => void;
}) {
  // Slice 35 — Identity fields are first because they're load-bearing for
  // the legal record. The Role section follows. Both can be edited in one
  // pass; on save the form dispatches one or both PATCHes in parallel
  // depending on what actually changed.
  const [name, setName] = useState(member.name);
  const [email, setEmail] = useState(member.email);
  const [reason, setReason] = useState('');
  const [kind, setKind] = useState<CgcMemberKind>(member.kind as CgcMemberKind);
  const [modesAllowed, setModesAllowed] = useState<string[]>(Array.from(member.modesAllowed));
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: (input: UpdateCgcMemberRequest) =>
      api.patch(`/api/admin/cgc-members/${member.id}`, input),
    onError: (e) => {
      setError(e instanceof ApiError ? translateEditError(e) : 'Update failed.');
    },
  });

  const updateIdentity = useMutation({
    mutationFn: (input: UpdateCgcMemberIdentityRequest) =>
      api.patch<{ member: AdminCgcMemberSummary }>(
        `/api/admin/cgc-members/${member.id}/identity`,
        input,
      ),
    onError: (e) => {
      setError(e instanceof ApiError ? translateIdentityError(e) : 'Identity update failed.');
    },
  });

  // Identity changes are gated client-side by whether name/email actually
  // differ from the loaded snapshot. Reason is required only when name/email
  // changed AND the member is not Placeholder — Placeholder edits are
  // pre-paperwork housekeeping (server enforces the same rule).
  const nameChanged = name.trim() !== member.name;
  const emailChanged = email.trim().toLowerCase() !== member.email.toLowerCase();
  const identityChanged = nameChanged || emailChanged;
  const reasonRequired = identityChanged && member.status !== 'Placeholder';

  async function submit() {
    setError(null);

    // Identity payload.
    const identityPatch: UpdateCgcMemberIdentityRequest = {};
    if (nameChanged) identityPatch.name = name.trim();
    if (emailChanged) identityPatch.email = email.trim().toLowerCase();
    if (reasonRequired) {
      if (!reason.trim()) {
        setError('A reason is required when renaming an Active member.');
        return;
      }
      identityPatch.reason = reason.trim();
    } else if (reason.trim()) {
      identityPatch.reason = reason.trim();
    }

    // Role payload — same shape + change-detection as pre-Slice-35.
    const before = [...member.modesAllowed].sort().join('|');
    const after = [...modesAllowed].sort().join('|');
    const rolePatch: { kind?: CgcMemberKind; modesAllowed?: string[] } = {};
    if (kind !== member.kind) rolePatch.kind = kind;
    if (after !== before) rolePatch.modesAllowed = modesAllowed;
    const roleChanged = rolePatch.kind !== undefined || rolePatch.modesAllowed !== undefined;

    if (!identityChanged && !roleChanged) {
      setError('No changes to save.');
      return;
    }

    // Validate both payloads before firing either — fail fast on
    // client-side issues without leaving a half-applied state.
    if (identityChanged) {
      const parsed = UpdateCgcMemberIdentitySchema.safeParse(identityPatch);
      if (!parsed.success) {
        setError(parsed.error.issues[0]?.message ?? 'Invalid identity input.');
        return;
      }
    }
    if (roleChanged) {
      const parsed = UpdateCgcMemberSchema.safeParse(rolePatch);
      if (!parsed.success) {
        setError(parsed.error.issues[0]?.message ?? 'Invalid role input.');
        return;
      }
    }

    // Fire both in parallel when both changed. If one fails the user sees
    // the error and the other half's success is still visible (we
    // invalidate cgc-members on `onDone`, which re-renders the row from
    // server state). Not transactional; the trade-off is documented on
    // the route handlers.
    const promises: Promise<unknown>[] = [];
    if (identityChanged) promises.push(updateIdentity.mutateAsync(identityPatch));
    if (roleChanged) promises.push(update.mutateAsync(rolePatch));
    const results = await Promise.allSettled(promises);
    const failed = results.some((r) => r.status === 'rejected');
    if (!failed) onDone();
  }

  function toggleMode(mode: string) {
    setModesAllowed((prev) =>
      prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode],
    );
  }

  function changeKind(next: CgcMemberKind) {
    setKind(next);
    const validForNext = new Set(MODES_FOR_KIND[next]);
    setModesAllowed((prev) => prev.filter((m) => validForNext.has(m)));
  }

  const saving = update.isPending || updateIdentity.isPending;

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          Identity
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name">
            {(id, desc) => (
              <TextInput
                id={id}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
                {...desc}
              />
            )}
          </Field>
          <Field label="Email">
            {(id, desc) => (
              <TextInput
                id={id}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
                {...desc}
              />
            )}
          </Field>
          {identityChanged ? (
            <Field
              label={reasonRequired ? 'Reason (required)' : 'Reason (optional)'}
              hint={
                reasonRequired
                  ? "Required when renaming a non-Placeholder member. The audit row IS the legal record — describe the change (e.g. 'Legal paperwork closed; placeholder identity Dr. Jane Doe corrected to actual name on certification')."
                  : 'Optional — Placeholder members are pre-paperwork housekeeping, but capturing context helps future readers.'
              }
            >
              {(id, desc) => (
                <textarea
                  id={id}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
                  {...desc}
                />
              )}
            </Field>
          ) : null}
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          Role
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Kind" hint={KIND_INFO[kind].description}>
            {(id, desc) => (
              <Select
                id={id}
                value={kind}
                onChange={(e) => changeKind(e.target.value as CgcMemberKind)}
                {...desc}
              >
                {CGC_MEMBER_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_INFO[k].label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label="Restrict to specific modes"
            hint="Empty = allow every mode this kind can approve. Changing kind may require clearing the list first."
          >
            {(id, desc) => (
              <fieldset id={id} className="grid grid-cols-1 gap-2" {...desc}>
                {MODES_FOR_KIND[kind].length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    {KIND_INFO[kind].label} members do not currently approve any system-prompt
                    modes.
                  </p>
                ) : (
                  MODES_FOR_KIND[kind].map((mode) => (
                    <label key={mode} className="flex items-start gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={modesAllowed.includes(mode)}
                        onChange={() => toggleMode(mode)}
                      />
                      <span>
                        <span className="font-medium">{mode}</span>
                        {MODE_DESCRIPTIONS[mode as PromptApprovalMode] ? (
                          <span className="block text-xs text-muted-foreground">
                            {MODE_DESCRIPTIONS[mode as PromptApprovalMode]}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  ))
                )}
              </fieldset>
            )}
          </Field>
        </div>
      </div>
      {error ? (
        <p className="text-sm text-traffic-red" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} loading={saving}>
          Save changes
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error translators — keep API error codes out of the UI. Centralised here
// so the row component stays readable; each takes an ApiError and returns
// human copy.
// ---------------------------------------------------------------------------

// Zod's default messages ("String must contain at least 1 character(s)",
// "Invalid email") are technically correct but not friendly. Translate the
// realistic invite-form failures to curated copy keyed by field path. Kind
// and modesAllowed are constrained by their UI controls (select + checkboxes)
// so they're not realistic failure paths — only name + email need humanising.
function humaniseInviteIssue(issue: { path: PropertyKey[]; message: string }): string {
  const field = issue.path[0];
  if (field === 'name') return 'Please enter a name.';
  if (field === 'email') return 'Please enter a valid email address.';
  if (field === 'kind') return 'Please choose a kind.';
  return issue.message;
}

function translateInviteError(e: ApiError): string {
  switch (e.message) {
    case 'email_already_exists':
      return 'A CGC member with that email already exists.';
    case 'invalid_modes_for_kind':
      return 'One or more selected modes are not permitted for the chosen kind.';
    case 'invalid_body':
      return 'Please fill in name, email, and kind.';
    default:
      return e.message || 'Invite failed.';
  }
}

function translateEditError(e: ApiError): string {
  switch (e.message) {
    case 'invalid_modes_for_kind':
      return 'Selected modes include ones not permitted for the chosen kind. Clear mode overrides or pick a compatible kind.';
    case 'not_found':
      return 'Member no longer exists.';
    default:
      return e.message || 'Update failed.';
  }
}

function translateResendError(e: ApiError): string {
  switch (e.message) {
    case 'already_enrolled':
      return 'Member has already completed TOTP enrolment.';
    case 'suspended':
      return 'Member is suspended. Reactivate them before resending an invite.';
    case 'not_found':
      return 'Member no longer exists.';
    default:
      return e.message || 'Resend failed.';
  }
}

// Slice 35 — promote-to-Active failures. `not_placeholder` is the
// concurrent-promote race (or operator confusion — clicking Promote on a row
// that is already Active). `not_found` is the member-was-revoked race.
function translatePromoteError(e: ApiError): string {
  switch (e.message) {
    case 'not_placeholder':
      return 'Member is no longer a Placeholder (already promoted, or status changed). Reload and try again.';
    case 'not_found':
      return 'Member no longer exists.';
    default:
      return e.message || 'Promote failed.';
  }
}

// Slice 35 — identity-update failures. `reason_required_for_active_member`
// fires when an Active or Suspended member is renamed without supplying a
// reason — Placeholder renames don't require it, but legal-paperwork renames
// on real members do (the audit row IS the legal record).
function translateIdentityError(e: ApiError): string {
  switch (e.message) {
    case 'reason_required_for_active_member':
      return 'A reason is required when renaming an Active member. Describe the legal-paperwork change.';
    case 'email_already_exists':
      return 'A CGC member with that email already exists.';
    case 'no_changes':
      return 'No identity changes to save.';
    case 'not_found':
      return 'Member no longer exists.';
    default:
      return e.message || 'Identity update failed.';
  }
}

function translateRevokeError(e: ApiError): string {
  switch (e.message) {
    case 'cannot_revoke_after_enrolment':
      return 'Cannot revoke — member has signed in. Use Suspend instead.';
    case 'cannot_revoke_with_decisions':
      return 'Cannot revoke — member has filed decisions. Use Suspend instead.';
    case 'cannot_revoke_with_classifier_reviews':
      return 'Cannot revoke — member has approved a classifier category. Use Suspend instead.';
    case 'cannot_revoke_with_qa_reviews':
      return 'Cannot revoke — member has filed QA rubric reviews or scores. Use Suspend instead.';
    case 'cannot_revoke_with_identity_history':
      return 'Cannot revoke — member has an identity-change audit history. Use Suspend instead.';
    case 'not_found':
      return 'Member no longer exists.';
    default:
      return e.message || 'Revoke failed.';
  }
}
